
import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Product } from "@/lib/types";
import ProductCard from "@/components/product/ProductCard";
import RevealOnScroll from "@/components/ui/RevealOnScroll";

export default function ProductRail({
  title,
  subtitle,
  products,
}: {
  title: string;
  subtitle?: string;
  products: Product[];
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: 1 | -1) => {
    scrollerRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (products.length === 0) return null;

  return (
    <section className="py-16 md:py-20">
      <div className="nm-container flex items-end justify-between mb-8">
        <RevealOnScroll>
          <h2 className="font-display text-2xl md:text-4xl font-semibold text-white">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-2 text-sm text-[var(--nm-ink-muted)]">{subtitle}</p>
          )}
        </RevealOnScroll>
        <div className="hidden md:flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollBy(-1)}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white hover:border-white/40 transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollBy(1)}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white hover:border-white/40 transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </motion.button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="nm-scrollbar-none nm-container flex gap-4 md:gap-5 overflow-x-auto pb-2 snap-x snap-mandatory cursor-grab active:cursor-grabbing"
      >
        {products.map((p) => (
          <div
            key={p.id}
            className="snap-start shrink-0 w-[62vw] sm:w-[38vw] md:w-[24vw] lg:w-[19vw]"
          >
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </section>
  );
}
